import { useEffect, useState } from "react";
import { ArrowUpRight, Save, Trash2, Image as ImageIcon } from "lucide-react";
import BookingCalendar from "@/components/BookingCalendar";
import AlbumManager from "@/components/AlbumManager";
import HomepagePhotosManager from "@/components/HomepagePhotosManager";
import adminApi, { errorText, setToken } from "@/adminApi";
import "@/AdminWorkspace.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const areas = [
  ["categories", "Homepage Categories"],
  ["featured", "Featured Frame"],
  ["media", "Homepage Photos"],
  ["album", "Albums (/album)"],
  ["services", "Services"],
  ["reviews", "Reviews"],
  ["reels", "Instagram Reels"],
  ["about", "About / Vignesh"],
  ["branding", "Branding"],
  ["contact", "Contact Settings"],
  ["hero", "Hero Media"],
  ["availability", "Availability"],
  ["enquiries", "Enquiries"],
  ["owner", "Ownership"],
];

const fields = {
  categories: ["title", "tagline", "image", "alt", "order", "visible"],
  featured: ["title", "caption", "label", "image"],
  media: ["title", "caption", "category", "alt", "homepage", "published", "order"],
  services: ["title", "description", "photo", "order", "visible"],
  reviews: ["reviewer", "rating", "review_text", "review_date", "review_link", "order", "visible"],
  reels: ["url", "image", "order", "visible"],
  about: ["title", "text", "image"],
  branding: ["logo"],
  contact: ["phone", "email", "address", "hours", "instagram", "google_maps", "apple_maps", "footer_note"],
  hero: ["video", "poster"],
};

const MEDIA_FIELDS = new Set(["image", "logo", "photo", "poster", "backdrop", "video"]);
const BOOL_FIELDS = new Set(["visible", "homepage", "published"]);
const NUMBER_FIELDS = new Set(["rating", "order"]);
const LONG_FIELDS = new Set(["review_text", "description", "caption", "address"]);
const BOOL_LABELS = { visible: "SHOW ON WEBSITE", homepage: "SHOW ON HOMEPAGE", published: "PUBLISHED" };

const label = (key) => key.replaceAll("_", " ").toUpperCase();

const mediaUrl = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (s.startsWith("/api/media/drive/") || s.startsWith("/api/")) {
    return `${API.replace("/api", "")}${s}`;
  }
  if (s.startsWith("http")) return s;
  return "";
};

export default function AdminWorkspace() {
  const [data, setData] = useState(null);
  const [area, setArea] = useState("categories");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/data", { params: { t: Date.now() } })
      .then((r) => setData(r.data))
      .catch(() => {
        setToken(null);
        window.location.href = "/admin/login";
      });
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(""), 4000);
    return () => clearTimeout(t);
  }, [message]);

  if (!data)
    return (
      <div className="loading" data-testid="admin-workspace-loading">
        Opening owner dashboard…
      </div>
    );

  const list = Array.isArray(data[area]) ? data[area] : [];

  const refresh = () =>
    adminApi
      .get("/admin/data", { params: { t: Date.now() } })
      .then((r) => setData(r.data));

  const save = async (id, payload) => {
    setBusy(true);
    try {
      const res = id
        ? await adminApi.put(`/admin/content/${area}/${id}`, { data: payload })
        : await adminApi.post(`/admin/content/${area}`, { data: payload });
      setMessage("Saved & published. The public site now uses this value.");
      await refresh();
      return res.data;
    } catch (e) {
      setMessage(errorText(e));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this item permanently?")) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/content/${area}/${id}`);
      setMessage("Removed.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const importDrive = async () => {
    const url = window.prompt("Paste a Google Drive sharing link (Anyone with the link — Viewer)");
    if (!url) return;
    setBusy(true);
    setMessage("Importing from Google Drive…");
    try {
      await adminApi.post("/admin/import", { drive_url: url, category: "Other" });
      setMessage("Imported. Set SHOW ON HOMEPAGE on the photo you want featured.");
      await refresh();
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const addNew = () => {
    const defaults = {
      reviews: { reviewer: "New reviewer", rating: 5, review_text: "", review_date: "", review_link: "", order: (data.reviews || []).length, visible: true },
      reels: { url: "", image: "", order: (data.reels || []).length, visible: true },
      services: { title: "New service", description: "", photo: "", order: (data.services || []).length, visible: true },
    };
    if (defaults[area]) save(null, defaults[area]).catch(() => {});
  };

  const areaLabel = areas.find((x) => x[0] === area)?.[1];

  return (
    <main className="editor">
      <header className="editor-top">
        <a href="/" className="wordmark">
          INFINITE<span>PHOTOGRAPHY</span>
        </a>
        <a href="/" data-testid="editor-view-site">VIEW SITE ↗</a>
        <button
          type="button"
          className="editor-logout"
          onClick={async () => {
            try {
              await adminApi.post("/auth/logout");
            } catch (_) {
              /* session already gone */
            }
            setToken(null);
            window.location.href = "/admin/login";
          }}
          data-testid="admin-logout-button"
        >
          LOG OUT
        </button>
      </header>
      <div className="editor-layout">
        <aside className="editor-nav">
          <p className="eyebrow">OWNER / ADMIN</p>
          {areas.map(([id, lbl]) => (
            <button
              className={area === id ? "active" : ""}
              key={id}
              onClick={() => {
                setArea(id);
                setMessage("");
              }}
              data-testid={`editor-${id}-tab`}
            >
              {lbl}
            </button>
          ))}
        </aside>
        <section className="editor-main">
          <div className="editor-heading">
            <div>
              <p className="eyebrow">CONTENT MANAGEMENT</p>
              <h1>{areaLabel}</h1>
              <p className="muted editor-help">{HELP[area]}</p>
            </div>
            <div className="editor-heading-actions">
              {["hero", "branding"].includes(area) && (
                <button
                  className="gold-btn"
                  onClick={importDrive}
                  disabled={busy}
                  data-testid="editor-drive-import"
                >
                  ADD FROM GOOGLE DRIVE <ArrowUpRight size={15} />
                </button>
              )}
              {["reviews", "reels", "services"].includes(area) && (
                <button
                  className="gold-btn"
                  onClick={addNew}
                  disabled={busy}
                  data-testid={`editor-${area}-add`}
                >
                  ADD {area.slice(0, -1).toUpperCase()} <ArrowUpRight size={15} />
                </button>
              )}
            </div>
          </div>
          {message && (
            <p className="notice" data-testid="editor-message">
              {message}
            </p>
          )}
          {area === "album" ? (
            <AlbumManager setMessage={setMessage} />
          ) : area === "media" ? (
            <HomepagePhotosManager items={data.media} refresh={refresh} setMessage={setMessage} />
          ) : area === "availability" ? (
            <AvailabilityEditor data={data} setMessage={setMessage} refresh={refresh} />
          ) : area === "enquiries" ? (
            <Enquiries items={data.enquiries} />
          ) : area === "owner" ? (
            <Ownership setMessage={setMessage} />
          ) : (
            <div className="editor-list">
              {list.map((item) => (
                <EditorRow
                  key={item.id}
                  item={item}
                  fields={fields[area] || ["title", "description"]}
                  area={area}
                  onSave={save}
                  onRemove={remove}
                  busy={busy}
                />
              ))}
              {list.length === 0 && (
                <p className="muted">Nothing yet — use the buttons above to add.</p>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

const HELP = {
  categories: "Rename the four homepage category titles freely, change the tagline, photo or alt text. Internal ids never change, so renamed titles stay renamed. Paste a Google Drive share link in IMAGE and click SAVE.",
  album: "The public /album archive — photos and videos, separate from the homepage. Import single or multiple Google Drive links, set category, reorder, publish or hide. Album media never appears on the homepage.",
  featured: "Update the Featured Frame photo and copy. Paste a Google Drive link in IMAGE.",
  media: "The curated homepage photographs. Toggle SHOW ON HOMEPAGE (max 10) or PUBLISHED to hide.",
  services: "Add, remove or reorder the services shown to visitors.",
  reviews: "Manage genuine Google reviews. Paste the complete review into REVIEW TEXT — the homepage automatically shows a 3-line preview with a MORE button when it's longer.",
  reels: "Paste each Reel URL and a Google Drive photo for the tile backdrop.",
  about: "The 'Meet Vignesh' section on the homepage.",
  branding: "The one place that controls the logo used in the header and footer. Paste a Google Drive image link and SAVE.",
  contact: "Phone, email, address, hours, Instagram profile URL, map destinations and the footer line. These values are used across the homepage, contact section and footer.",
  hero: "Replace the homepage hero video. Paste a Google Drive video link in VIDEO and a photo link in POSTER.",
  availability: "Mark dates as Available, Limited, Booked or Pending. Visitors see the colours instantly.",
  enquiries: "All booking requests from the website.",
  owner: "Invite another owner and safely hand over control.",
};

function MediaPreview({ url }) {
  const src = mediaUrl(url);
  if (!src) {
    return (
      <div className="media-thumb empty" aria-hidden>
        <ImageIcon size={16} />
      </div>
    );
  }
  if (String(url).match(/\.(mp4|webm|mov)$/i)) {
    return <video className="media-thumb" src={src} muted playsInline />;
  }
  return <img className="media-thumb" src={src} alt="preview" onError={(e) => (e.currentTarget.style.opacity = 0.15)} />;
}

function EditorRow({ item, fields, area, onSave, onRemove, busy }) {
  const [draft, setDraft] = useState(item);
  const [dirty, setDirty] = useState(false);

  // Re-sync draft when the underlying item changes (after refresh)
  useEffect(() => {
    setDraft(item);
    setDirty(false);
  }, [item]);

  const setField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const submit = async () => {
    try {
      await onSave(item.id, draft);
      setDirty(false);
    } catch (_) {
      /* handled by parent */
    }
  };

  const title = item.title || item.name || item.reviewer || item.url || item.id;

  return (
    <article className="editor-row" data-testid={`editor-row-${item.id || "new"}`}>
      <header className="editor-row-head">
        <strong>{title}</strong>
        {dirty && <span className="dirty-tag">UNSAVED CHANGES</span>}
      </header>
      <div className="editor-fields">
        {fields.map((key) => {
          const value = draft[key];
          if (BOOL_FIELDS.has(key)) {
            return (
              <label key={key} className="editor-toggle">
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => setField(key, e.target.checked)}
                  data-testid={`editor-${area}-${key}-input`}
                />
                {BOOL_LABELS[key] || label(key)}
              </label>
            );
          }
          if (NUMBER_FIELDS.has(key)) {
            return (
              <label key={key}>
                {label(key)}
                <input
                  type="number"
                  value={value ?? 0}
                  onChange={(e) => setField(key, e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid={`editor-${area}-${key}-input`}
                />
              </label>
            );
          }
          if (MEDIA_FIELDS.has(key)) {
            return (
              <label key={key} className="editor-media-field">
                {label(key)}
                <div className="editor-media-row">
                  <MediaPreview url={value} />
                  <input
                    value={value ?? ""}
                    placeholder="Paste Google Drive share link"
                    onChange={(e) => setField(key, e.target.value)}
                    data-testid={`editor-${area}-${key}-input`}
                  />
                </div>
                <small className="muted">
                  Share as “Anyone with the link — Viewer”, paste the URL, then SAVE.
                </small>
              </label>
            );
          }
          if (LONG_FIELDS.has(key)) {
            return (
              <label key={key} className="editor-long">
                {label(key)}
                <textarea
                  value={value ?? ""}
                  onChange={(e) => setField(key, e.target.value)}
                  data-testid={`editor-${area}-${key}-input`}
                />
              </label>
            );
          }
          return (
            <label key={key}>
              {label(key)}
              <input
                value={value ?? ""}
                onChange={(e) => setField(key, e.target.value)}
                data-testid={`editor-${area}-${key}-input`}
              />
            </label>
          );
        })}
      </div>
      <div className="editor-actions">
        <button
          className="gold-btn"
          onClick={submit}
          disabled={busy || !dirty}
          data-testid={`editor-save-${item.id || "new"}`}
        >
          <Save size={14} /> {dirty ? "SAVE CHANGES" : "SAVED"}
        </button>
        {item.id && (
          <button
            className="delete-btn"
            onClick={() => onRemove(item.id)}
            disabled={busy}
            data-testid={`editor-delete-${item.id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </article>
  );
}

function AvailabilityEditor({ data, setMessage, refresh }) {
  const [selected, setSelected] = useState([]);
  const [status, setStatus] = useState("AVAILABLE");

  const save = async () => {
    if (selected.length === 0) {
      setMessage("Select at least one date on the calendar first.");
      return;
    }
    try {
      await adminApi.put("/admin/availability/bulk", { dates: selected, status });
      setMessage(`Saved ${status} for ${selected.length} date(s).`);
      setSelected([]);
      refresh();
    } catch (e) {
      setMessage(errorText(e));
    }
  };

  const clearConfigured = async (date) => {
    await adminApi.delete(`/admin/availability/${date}`);
    setMessage(`Cleared ${date}`);
    refresh();
  };

  return (
    <div className="editor-form availability-editor">
      <p className="muted">
        Pick single dates, multiple non-contiguous dates or a continuous range,
        then apply a status. Unconfigured dates always show visitors
        "Availability to Be Confirmed".
      </p>

      <div className="availability-picker">
        <BookingCalendar
          availability={data.availability || []}
          selected={selected}
          onChange={setSelected}
          testIdPrefix="admin-cal"
        />

        <div className="availability-actions">
          <label>
            STATUS TO APPLY
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="admin-availability-status"
            >
              <option>AVAILABLE</option>
              <option>LIMITED</option>
              <option>BOOKED</option>
              <option>PENDING CONFIRMATION</option>
            </select>
          </label>
          <button
            className="gold-btn"
            type="button"
            onClick={save}
            data-testid="admin-availability-save"
          >
            APPLY TO SELECTED <Save size={14} />
          </button>
        </div>
      </div>

      <div className="availability-list">
        <p className="eyebrow">CONFIGURED DATES · {(data.availability || []).length}</p>
        {(data.availability || []).length === 0 && (
          <p className="muted">No dates configured yet.</p>
        )}
        {(data.availability || [])
          .slice()
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((a) => (
            <div key={a.date} className="availability-row" data-testid={`admin-avail-row-${a.date}`}>
              <strong>{a.date}</strong>
              <span>{a.status}</span>
              <button
                type="button"
                onClick={() => clearConfigured(a.date)}
                data-testid={`admin-avail-clear-${a.date}`}
              >
                <Trash2 size={12} /> CLEAR
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}

function Enquiries({ items }) {
  return (
    <div className="editor-list">
      {(items || []).map((x) => {
        const dates = Array.isArray(x.requested_dates) && x.requested_dates.length
          ? x.requested_dates
          : (x.requested_date ? [x.requested_date] : []);
        return (
          <article className="enquiry" key={x.id} data-testid="editor-enquiry">
            <strong>
              {x.name} · {x.shoot_type}
            </strong>
            <span>
              {dates.length ? dates.join(", ") : "Dates to be confirmed"} · {x.venue}
            </span>
            <small>
              {x.phone} · {x.email}
            </small>
            <p>{x.message}</p>
          </article>
        );
      })}
      {(!items || items.length === 0) && <p className="muted">No enquiries yet.</p>}
    </div>
  );
}

function Ownership({ setMessage }) {
  const [email, setEmail] = useState("");
  return (
    <div className="editor-form">
      <p className="muted">
        Invite the client owner first. Their setup link expires in one hour.
        Remove the previous development owner only after access is verified.
      </p>
      <label>
        NEW OWNER EMAIL
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="ownership-email-input"
        />
      </label>
      <button
        className="gold-btn"
        onClick={async () => {
          try {
            await adminApi.post("/admin/ownership/transfer", { email });
            setMessage("Secure ownership invitation sent");
          } catch (e) {
            setMessage(errorText(e));
          }
        }}
        data-testid="ownership-transfer-button"
      >
        SEND OWNER INVITE <ArrowUpRight size={15} />
      </button>
    </div>
  );
}
